/**
 * Once-per-unit exposure bookkeeping for experiments.
 *
 * Assignment is not exposure: `experiment_started` must fire when the variant
 * actually RENDERS, exactly once per (unit, experiment, variant) — otherwise
 * the funnel denominator includes people who never saw the thing and every
 * lift dilutes toward zero. Persisted in localStorage so reloads and new
 * sessions don't refire; a *changed* variant (flag retargeted mid-experiment)
 * refires so reassignment stays visible in the data.
 */
const STORAGE_PREFIX = 'predictions:exp_exposed:';

/** In-memory mirror; also the fallback when localStorage is unavailable. */
const memory = new Map<string, string>();

function storageKey(experimentKey: string): string {
  return `${STORAGE_PREFIX}${experimentKey}`;
}

/**
 * Record an exposure. Returns true when this (unit, variant, fallback-ness)
 * triple has not been recorded for the experiment yet — i.e. the caller
 * should track. `isFallback` is part of the identity so a grace-timeout
 * fallback exposure (`control`, fallback) does not swallow the real
 * evaluation that settles moments later (`control`, real) — otherwise the
 * unit would be stuck recorded as fallback traffic forever.
 */
export function markExposedOnce(experimentKey: string, variant: string, unitId: string, isFallback: boolean): boolean {
  const value = `${unitId}:${variant}:${isFallback ? 'fallback' : 'real'}`;
  const key = storageKey(experimentKey);

  let previous = memory.get(key);
  if (previous === undefined) {
    try {
      previous = window.localStorage.getItem(key) ?? undefined;
    } catch {
      // storage unavailable — memory map is the only record
    }
  }
  if (previous === value) return false;

  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage unavailable — dedup degrades to per-tab, which is acceptable
  }
  return true;
}

/** Read the recorded exposure (`unitId:variant:fallback-ness`), if any. Devtool inspection. */
export function peekExposure(experimentKey: string): string | null {
  const key = storageKey(experimentKey);
  const inMemory = memory.get(key);
  if (inMemory !== undefined) return inMemory;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Forget an exposure so the next render re-reports it. Devtool + QA use. */
export function clearExposure(experimentKey: string): void {
  const key = storageKey(experimentKey);
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // storage unavailable
  }
}

/** Test-only escape hatch. */
export function __resetExposuresForTests(): void {
  for (const key of memory.keys()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // storage unavailable
    }
  }
  memory.clear();
  try {
    // Also clear entries persisted by earlier test runs / other instances.
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) window.localStorage.removeItem(key);
    }
  } catch {
    // storage unavailable
  }
}
